import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CustomAddressControl } from './CustomAddressControl';
import {
  GoogleMap,
  GoogleMarker,
  updateMarkerIfNeeded,
  updateZoomIfNeeded,
  InfoWindow,
} from './utils';
import { LocationItem, LocationCallLogMessage } from '../../services/api/types';

export interface errorObject {
  error: boolean;
  message?: string;
  showOnlyTooltip?: boolean;
  showLatlngTooltip?: boolean;
  showAddressTooltip?: boolean;
}

export interface MapProps {
  width: number | string;
  height: number | string;
  data: LocationItem | null;
  onPlaceChanged?: (
    place: LocationItem | null,
    logMessage: LocationCallLogMessage,
  ) => void;
  isCountryCodeRestrict: boolean;
}

export const Map = ({
  width,
  height,
  data,
  onPlaceChanged,
  isCountryCodeRestrict,
}: MapProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const ctrlRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<GoogleMap>();
  const [marker, setMarker] = useState<GoogleMarker>();
  const [infoWindow, setInfoWindow] = useState<InfoWindow>();
  const [zoom, setZoom] = useState<number>(4);
  const lat = Number(data?.latitude);
  const lng = Number(data?.longitude);
  const initMap = (): void => {
    if (ref.current && ctrlRef.current) {
      let latlngVal = { lat: 39.8097343, lng: -98.5556199 };
      let positionVal = null;
      if (lat && lng) {
        latlngVal = { lat, lng };
        setZoom(14);
        positionVal = { lat, lng };
      }
      const _map = new google.maps.Map(ref.current, {
        zoom: zoom,
        streetViewControl: false,
        center: latlngVal,
        rotateControl: false,
        scaleControl: false,
        fullscreenControl: false,
        panControl: false,
        zoomControl: true,
        gestureHandling: 'cooperative',
        draggableCursor: 'pointer',
        mapTypeControl: false,
      });
      const _marker = new google.maps.Marker({
        position: positionVal,
        map: _map,
      });
      _map.controls[google.maps.ControlPosition.TOP_LEFT].push(ctrlRef.current);
      setMarker(_marker);
      setMap(_map);
      const _infowindow = new google.maps.InfoWindow();
      setInfoWindow(_infowindow);
    }
  };
  const initCallback = useCallback(initMap, [lat, lng, zoom]);
  useEffect(() => {
    if (!map) {
      initCallback();
      return;
    }
    updateZoomIfNeeded(map, zoom);
    if (lat && lng) {
      updateMarkerIfNeeded(map, marker, lat, lng);
    }
  }, [map, marker, zoom, lat, lng, initCallback]);

  return (
    <>
      <div style={{ display: 'none' }}>
        <div ref={ctrlRef}>
          {map && marker && (
            <CustomAddressControl
              map={map}
              marker={marker}
              data={data}
              infoWindow={infoWindow}
              onPlaceChanged={onPlaceChanged}
              isCountryCodeRestrict={isCountryCodeRestrict}
            />
          )}
        </div>
      </div>
      <div className="map-container" style={{ width: width, height: height }}>
        <div
          ref={ref}
          className="map-container__map"
          style={{ width: width, height: height }}
        />
      </div>
    </>
  );
};
